import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import React, { lazy, Suspense, useContext, useState } from 'react';
import { UserContext } from './contexts/userContext';
import Login from './components/login';
import { IntlProvider } from 'react-intl';
import en from './lang/en.json';
import de from './lang/de.json';
import fr from './lang/fr.json';
import SuspenseLoader from './components/SuspenseLoader';
import SidebarLayout from './layouts/SidebarLayout';
import { TokenContext } from './contexts/tokenContext';
import InstallationTabs from './content/dashboards/Installations/index';
import routes from 'src/Resources/routes.json';
import './App.css';
import ForgotPassword from './components/ForgotPassword';
import { axiosConfigWithoutToken } from './Resources/axiosConfig';
import InstallationsContextProvider from './contexts/InstallationsContextProvider';
import AccessContextProvider from './contexts/AccessContextProvider';
import SalidomoInstallationTabs from './content/dashboards/SalidomoInstallations';
import SodioHomeInstallationTabs from './content/dashboards/SodiohomeInstallations';
import { ProductIdContext } from './contexts/ProductIdContextProvider';

function App() {
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const tokencontext = useContext(TokenContext);
  const { token, setNewToken } = tokencontext;
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get('username');
  const {
    setAccessToSalimax,
    setAccessToSalidomo,
    setAccessToSodiohome,
    setAccessToSodistore
  } = useContext(ProductIdContext);

  const [language, setLanguage] = useState('en');
  const getTranslations = () => {
    switch (language) {
      case 'en':
        return en;
      case 'de':
        return de;
      case 'fr':
        return fr;
    }
  };

  const Loader = (Component) => (props) =>
    (
      <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
      </Suspense>
    );

  const ResetPassword = Loader(
    lazy(() => import('src/components/ResetPassword'))
  );

  const SetNewPassword = Loader(
    lazy(() => import('src/components/SetNewPassword'))
  );

  const Login = Loader(lazy(() => import('src/components/login')));
  const Users = Loader(lazy(() => import('src/content/dashboards/Users')));

  const loginToResetPassword = () => {
    axiosConfigWithoutToken
      .post('/Login', null, { params: { username, password: '' } })
      .then((response) => {
        if (response.data && response.data.token) {
          setNewToken(response.data.token);
          setUser(response.data.user);
          setAccessToSalimax(response.data.accessToSalimax);
          setAccessToSalidomo(response.data.accessToSalidomo);
          setAccessToSodiohome(response.data.accessToSodiohome);
          setAccessToSodistore(response.data.accessToSodistoreMax);
          if (response.data.accessToSalimax) {
            navigate(routes.installations);
          } else if (response.data.accessToSalidomo) {
            navigate(routes.salidomo_installations);
          } else if (response.data.accessToSodistoreMax) {
            navigate(routes.sodistore_installations);
          } else {
            navigate(routes.sodiohome_installations);
          }
        }
      })
      .catch(() => {});
  };

  if (username) {
    loginToResetPassword();
  }

  if (!token) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <Routes>
          <Route
            path={''}
            element={<Navigate to={routes.login}></Navigate>}
          ></Route>
          <Route path={routes.login} element={<Login></Login>}></Route>
          <Route
            path={routes.forgotPassword}
            element={<ForgotPassword />}
          ></Route>
          <Route
            path={'*'}
            element={<Navigate to={routes.login}></Navigate>}
          ></Route>
        </Routes>
      </ThemeProvider>
    );
  }

  if (token && currentUser?.mustResetPassword) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <SetNewPassword></SetNewPassword>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <IntlProvider
        messages={getTranslations()}
        locale={language}
        defaultLocale="en"
      >
        <InstallationsContextProvider>
          <CssBaseline />
          <Routes>
            <Route
              path={''}
              element={<Navigate to={routes.installations}></Navigate>}
            ></Route>
            <Route
              path={'login'}
              element={<Navigate to={routes.installations}></Navigate>}
            ></Route>
            <Route
              path="/"
              element={
                <SidebarLayout
                  language={language}
                  onSelectLanguage={setLanguage}
                />
              }
            >
              <Route
                path={routes.installations + '*'}
                element={
                  <AccessContextProvider>
                    <InstallationTabs product={0} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodistore_installations + '*'}
                element={
                  <AccessContextProvider>
                    <InstallationTabs product={3} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.salidomo_installations + '*'}
                element={
                  <AccessContextProvider>
                    <SalidomoInstallationTabs product={1} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodiohome_installations + '*'}
                element={
                  <AccessContextProvider>
                    <SodioHomeInstallationTabs product={2} />
                  </AccessContextProvider>
                }
              />

              <Route path={routes.users + '*'} element={<Users />} />
              <Route
                path={'*'}
                element={<Navigate to={routes.installations}></Navigate>}
              ></Route>
              <Route path="ResetPassword" element={<ResetPassword />}></Route>
            </Route>
          </Routes>
        </InstallationsContextProvider>
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
